import React, { useEffect, useRef, useState } from 'react'
import { Plus } from 'sutra/components/Icons'

import {
  KeywordAddMoreButton,
  PaneDescription,
  PaneHeading,
  ScrollablePaneContainer,
} from 'features/charts-v2/components/filter-popover-pane.styles'
import { FilterPopoverPaneFooter } from 'features/charts-v2/components/FilterPopoverPaneFooter/FilterPopoverPaneFooter'
import { KeywordCriterionRow } from 'features/charts-v2/components/KeywordCriterionRow/KeywordCriterionRow'
import { type OperatorType as KeywordOperatorType } from 'features/charts-v2/components/KeywordOperatorPopover'
import { KEYWORD_OPERATORS } from 'features/charts-v2/utils/keyword-operators'
import { parseKeywordInput } from 'features/charts-v2/utils/parse-keyword-input'

interface FilterPopoverKeywordsPaneProps {
  keywordFilters: { [key: string]: string[] | null }
  onApply: (filters: {
    [key in KeywordOperatorType]?: string[] | null
  }) => void
  onClear: () => void
}

interface KeywordRowDraft {
  id: number
  operator: KeywordOperatorType
  values: string[]
}

// Builds the draft row list from the committed record: one row per operator
// with non-null values, in canonical KEYWORD_OPERATORS order; a single empty
// row (first operator) when nothing is applied yet.
const rowsFromCommitted = (
  keywordFilters: { [key: string]: string[] | null },
  nextId: () => number,
): KeywordRowDraft[] => {
  const rows: KeywordRowDraft[] = []
  for (const operator of KEYWORD_OPERATORS) {
    const values = keywordFilters[operator]
    if (values != null) {
      rows.push({ id: nextId(), operator, values })
    }
  }
  const fallbackOperator = KEYWORD_OPERATORS[0]
  if (rows.length === 0 && fallbackOperator) {
    rows.push({ id: nextId(), operator: fallbackOperator, values: [] })
  }
  return rows
}

// Re-derives the committed record in canonical KEYWORD_OPERATORS order (from
// either the draft rows or the `keywordFilters` prop) so the two are directly
// comparable regardless of row insertion order (rows appended via "Add more"
// don't land in operator order).
const toCanonicalRecord = (
  valuesByOperator: Map<KeywordOperatorType, string[]>,
): { [key in KeywordOperatorType]?: string[] } => {
  const record: { [key in KeywordOperatorType]?: string[] } = {}
  KEYWORD_OPERATORS.forEach((operator) => {
    const values = valuesByOperator.get(operator)
    if (values !== undefined) record[operator] = values
  })
  return record
}

// In-popover editor for Keywords. One compact row per active operator
// ([Operator select] [search terms] [X]), capped at one row per operator —
// `searchCriteria` is keyed by operator, so two rows can't share one (see
// AGENTS.md) — grown via "Add more". Nothing writes to the panel until
// Apply, which commits every row at once so a removed row can express a
// removal.
export const FilterPopoverKeywordsPane: React.FC<
  FilterPopoverKeywordsPaneProps
> = ({ keywordFilters, onApply, onClear }) => {
  const idCounterRef = useRef(0)
  const nextId = (): number => ++idCounterRef.current

  const [rows, setRows] = useState<KeywordRowDraft[]>(() =>
    rowsFromCommitted(keywordFilters, nextId),
  )

  // `keywordFilters` is a fresh reference on every render of the parent
  // pane, but its actual content only changes on a genuine external commit
  // (Apply/Clear). Mirror it into a ref (always current) and re-sync the
  // draft only when its serialized content actually changes — not on every
  // unrelated popover re-render, which would otherwise clobber in-progress
  // edits.
  const keywordFiltersRef = useRef(keywordFilters)
  keywordFiltersRef.current = keywordFilters
  const committedKey = JSON.stringify(keywordFilters)
  useEffect(() => {
    setRows(rowsFromCommitted(keywordFiltersRef.current, nextId))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keywordFiltersRef/nextId are stable ref-backed helpers, not reactive dependencies; only committedKey should re-trigger this sync
  }, [committedKey])

  const usedOperators = new Set(rows.map((row) => row.operator))
  const availableOperators = KEYWORD_OPERATORS.filter(
    (operator) => !usedOperators.has(operator),
  )

  const handleOperatorChange = (
    rowId: number,
    newOperator: KeywordOperatorType,
  ): void => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, operator: newOperator } : row,
      ),
    )
  }

  const handleValuesChange = (rowId: number, newValues: string[]): void => {
    const cleaned = Array.from(new Set(newValues.flatMap(parseKeywordInput)))
    setRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, values: cleaned } : row)),
    )
  }

  const handleRemoveRow = (rowId: number): void => {
    setRows((prev) => prev.filter((row) => row.id !== rowId))
  }

  const handleAddRow = (): void => {
    const nextOperator = availableOperators[0]
    if (!nextOperator) return
    setRows((prev) => [
      ...prev,
      { id: nextId(), operator: nextOperator, values: [] },
    ])
  }

  // A row with no values yet (the default empty row, or one just added via
  // "Add more") doesn't count as a real criterion — it must compare equal to
  // "not applied" so Apply stays disabled until something is actually typed,
  // and so Apply sends `null` for it rather than an empty-array no-op.
  const draftRecord = toCanonicalRecord(
    new Map(
      rows
        .filter((row) => row.values.length > 0)
        .map((row) => [row.operator, row.values]),
    ),
  )
  // Mirror the same "empty counts as not applied" rule on the committed
  // side, so an untouched row compares equal to its committed counterpart
  // even when that counterpart happens to be an applied-but-empty card
  // (`values: []` — the sidebar's live KeywordCard can produce that state
  // independently of this pane; see FilterControl.handleKeywordFilterUpdate).
  const committedRecord = toCanonicalRecord(
    new Map(
      KEYWORD_OPERATORS.filter(
        (op) => (keywordFilters[op]?.length ?? 0) > 0,
      ).map((op) => [op, keywordFilters[op] as string[]]),
    ),
  )
  const isDirty =
    JSON.stringify(draftRecord) !== JSON.stringify(committedRecord)
  // Clear's job is removing every keyword card, including an applied-but
  // -empty one, so it uses the broader "key present at all" check rather
  // than the dirty-check's "has real content" one.
  const hasCommittedFilters = KEYWORD_OPERATORS.some(
    (operator) => keywordFilters[operator] != null,
  )

  const handleApply = (): void => {
    const filters: { [key in KeywordOperatorType]?: string[] | null } = {}
    KEYWORD_OPERATORS.forEach((operator) => {
      filters[operator] = draftRecord[operator] ?? null
    })
    onApply(filters)
  }

  const handleClear = (): void => {
    setRows(rowsFromCommitted({}, nextId))
    onClear()
  }

  return (
    <ScrollablePaneContainer>
      <PaneHeading>Keywords</PaneHeading>
      <PaneDescription>
        Matches posts by the literal keywords or phrases you enter.
      </PaneDescription>
      {rows.map((row) => (
        <KeywordCriterionRow
          key={row.id}
          operator={row.operator}
          values={row.values}
          usedOperators={usedOperators}
          onOperatorChange={(operator) =>
            handleOperatorChange(row.id, operator)
          }
          onValuesChange={(values) => handleValuesChange(row.id, values)}
          onRemove={() => handleRemoveRow(row.id)}
        />
      ))}
      <KeywordAddMoreButton
        type="button"
        variant="secondary"
        size="sm"
        height="30px"
        disabled={availableOperators.length === 0}
        onClick={handleAddRow}
      >
        <Plus /> Add more
      </KeywordAddMoreButton>
      <FilterPopoverPaneFooter
        applyDisabled={!isDirty}
        clearDisabled={!hasCommittedFilters}
        onApply={handleApply}
        onClear={handleClear}
      />
    </ScrollablePaneContainer>
  )
}
